/**
 * Helpers for the "Tell AI what to change" floating prompt.
 */

export interface PromptRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
  width: number;
  height: number;
}

export interface SelectionInfo {
  text: string;
  rect: PromptRect | null;
  iframeRect: PromptRect | null;
  fieldKey: string | null;
  slotType: "question" | "help" | "placeholder" | "text" | null;
}

/**
 * Returns the current selection inside the iframe, its bounding rect in host window viewport
 * coordinates, the iframe's bounding rect, and the nearest field key / slot type for contextual messaging.
 */
export const getSelectionAndRect = (
  iframe: HTMLIFrameElement | null
): SelectionInfo | null => {
  if (!iframe) return null;
  let doc: Document | null = null;
  try {
    doc = iframe.contentDocument;
  } catch {
    return null;
  }
  if (!doc) return null;

  const sel = doc.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
  const range = sel.getRangeAt(0);
  const text = range.toString().trim();
  if (!text) return null;

  const iframeRectRaw = iframe.getBoundingClientRect();
  if (iframeRectRaw.width === 0 && iframeRectRaw.height === 0) return null;

  const clientRects = range.getClientRects();
  const rangeBounding = range.getBoundingClientRect();

  if (rangeBounding.width === 0 && rangeBounding.height === 0 && clientRects.length === 0) {
    return null;
  }

  // Use the first line rect for anchoring above the selection, falling back to overall bounding rect
  const validRect = (r: DOMRect | { width: number; height: number } | null | undefined) =>
    !!r && (r.width > 0 || r.height > 0);
  const primaryRect =
    clientRects.length > 0 && validRect(clientRects[0]) ? clientRects[0] : rangeBounding;

  // Check if visible inside iframe viewport
  const viewHeight = iframe.clientHeight || iframeRectRaw.height;
  if (viewHeight > 0 && (primaryRect.bottom < 0 || primaryRect.top > viewHeight)) {
    return null;
  }

  const iframeRect: PromptRect = {
    top: iframeRectRaw.top,
    bottom: iframeRectRaw.bottom,
    left: iframeRectRaw.left,
    right: iframeRectRaw.right,
    width: iframeRectRaw.width,
    height: iframeRectRaw.height,
  };

  // Convert iframe-local coordinates to host viewport coordinates
  const rect: PromptRect = {
    top: iframeRectRaw.top + primaryRect.top,
    bottom: iframeRectRaw.top + primaryRect.bottom,
    left: iframeRectRaw.left + primaryRect.left,
    right: iframeRectRaw.left + primaryRect.right,
    width: primaryRect.width,
    height: primaryRect.height,
  };

  // Find nearest ancestor with data-field-key or wfx-q-<key>
  let fieldKey: string | null = null;
  let slotType: SelectionInfo["slotType"] = null;
  let node: Node | null = range.commonAncestorContainer;
  while (node && node !== doc.body) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.dataset?.fieldKey) {
        fieldKey = el.dataset.fieldKey;
        break;
      }
      if (el.id?.startsWith("wfx-q-")) {
        fieldKey = el.id.replace("wfx-q-", "");
        slotType = "question";
        break;
      }
      if (el.id?.startsWith("wfx-h-")) {
        fieldKey = el.id.replace("wfx-h-", "");
        slotType = "help";
        break;
      }
      if (el.classList.contains("wfx-t")) {
        // text slot (title, welcome, thank-you, section, submit)
        slotType = "text";
        break;
      }
    }
    node = (node as Element).parentElement || node.parentNode;
  }
  return { text, rect, iframeRect, fieldKey, slotType };
};

/**
 * Returns the user instruction directly without hardcoded wrapper prefixes.
 */
export const composeMessage = (
  _selectedText?: string | null,
  instruction: string = "",
  _fieldKey?: string | null,
  _slotType?: SelectionInfo["slotType"]
): string => {
  return truncateToLimit(instruction.trim());
};

/**
 * Truncates a message to the AI service limit (2000 chars).
 */
export const truncateToLimit = (msg: string, limit = 2000): string =>
  msg.length > limit ? msg.slice(0, limit - 1) + "…" : msg;
